import { SkillCategory } from '../../types/skillProgress';

export const racquetballSkillCategories: SkillCategory[] = [
  {
    id: 'serving',
    name: 'Serving',
    description: 'Master the art of serving in racquetball',
    skills: [
      {
        id: 'basic-serve',
        name: 'Basic Serve',
        description: 'Execute a legal serve',
        difficulty: 'beginner',
        tips: [
          'Stand in the service box',
          'Hit the ball to the front wall first',
          'Keep one foot in the service box',
          'Aim for the opposite back corner'
        ]
      },
      {
        id: 'serve-placement',
        name: 'Serve Placement',
        description: 'Place serves to different areas',
        difficulty: 'beginner',
        prerequisites: ['basic-serve'],
        tips: [
          'Aim for the back corners',
          'Use the side walls for angles',
          'Mix up placement to keep opponents guessing',
          'Practice consistency over power'
        ]
      },
      {
        id: 'power-serve',
        name: 'Power Serve',
        description: 'Add pace to your serve',
        difficulty: 'intermediate',
        prerequisites: ['basic-serve', 'serve-placement'],
        tips: [
          'Generate power from your legs and core',
          'Use proper weight transfer',
          'Keep your head still during contact',
          'Follow through completely'
        ]
      },
      {
        id: 'spin-serve',
        name: 'Spin Serve',
        description: 'Master different types of spin serves',
        difficulty: 'advanced',
        prerequisites: ['power-serve'],
        tips: [
          'Practice topspin and backspin',
          'Use spin to control bounce',
          'Vary spin to keep opponents guessing',
          'Focus on consistency and placement'
        ]
      }
    ]
  },
  {
    id: 'strokes',
    name: 'Strokes',
    description: 'Develop consistent stroke technique',
    skills: [
      {
        id: 'forehand-basic',
        name: 'Basic Forehand',
        description: 'Execute a proper forehand stroke',
        difficulty: 'beginner',
        tips: [
          'Use a continental grip',
          'Keep your racket face square to the ball',
          'Follow through across your body',
          'Stay balanced throughout the stroke'
        ]
      },
      {
        id: 'backhand-basic',
        name: 'Basic Backhand',
        description: 'Execute a proper backhand stroke',
        difficulty: 'beginner',
        tips: [
          'Use a continental or eastern backhand grip',
          'Keep your non-dominant hand on the racket',
          'Rotate your shoulders for power',
          'Follow through toward your target'
        ]
      },
      {
        id: 'volley-basic',
        name: 'Basic Volley',
        description: 'Execute a proper volley',
        difficulty: 'intermediate',
        prerequisites: ['forehand-basic', 'backhand-basic'],
        tips: [
          'Keep your racket up and ready',
          'Use a short, compact swing',
          'Step into the ball',
          'Keep your eyes on the ball'
        ]
      },
      {
        id: 'kill-shot',
        name: 'Kill Shot',
        description: 'Execute a powerful kill shot',
        difficulty: 'advanced',
        prerequisites: ['volley-basic'],
        tips: [
          'Hit the ball low on the front wall',
          'Use maximum power and speed',
          'Aim for the corners',
          'Follow through completely'
        ]
      }
    ]
  },
  {
    id: 'movement',
    name: 'Movement',
    description: 'Master court movement and positioning',
    skills: [
      {
        id: 'basic-footwork',
        name: 'Basic Footwork',
        description: 'Develop fundamental court movement',
        difficulty: 'beginner',
        tips: [
          'Stay on your toes',
          'Use small, quick steps',
          'Always return to the center',
          'Keep your weight forward'
        ]
      },
      {
        id: 'lunge-technique',
        name: 'Lunge Technique',
        description: 'Master the forward lunge',
        difficulty: 'intermediate',
        prerequisites: ['basic-footwork'],
        tips: [
          'Step forward with your racket foot',
          'Keep your knee over your toe',
          'Use your lunge to reach the ball',
          'Push back to center court'
        ]
      },
      {
        id: 'court-coverage',
        name: 'Court Coverage',
        description: 'Cover the entire court efficiently',
        difficulty: 'intermediate',
        prerequisites: ['lunge-technique'],
        tips: [
          'Anticipate your opponent\'s shots',
          'Use efficient movement patterns',
          'Stay balanced and ready',
          'Practice court coverage drills'
        ]
      },
      {
        id: 'advanced-movement',
        name: 'Advanced Movement',
        description: 'Master complex movement patterns',
        difficulty: 'advanced',
        prerequisites: ['court-coverage'],
        tips: [
          'Use deceptive movement',
          'Master quick direction changes',
          'Develop explosive power',
          'Practice under pressure'
        ]
      }
    ]
  },
  {
    id: 'strategy',
    name: 'Strategy & Tactics',
    description: 'Develop strategic thinking and court awareness',
    skills: [
      {
        id: 'court-positioning',
        name: 'Court Positioning',
        description: 'Understand proper court positioning',
        difficulty: 'beginner',
        tips: [
          'Stay in the center of the court',
          'Move efficiently to the ball',
          'Return to center after each shot',
          'Anticipate your opponent\'s shots'
        ]
      },
      {
        id: 'shot-selection',
        name: 'Shot Selection',
        description: 'Choose the right shot for each situation',
        difficulty: 'intermediate',
        prerequisites: ['court-positioning'],
        tips: [
          'Consider your opponent\'s position',
          'Use the right shot for the right situation',
          'Don\'t try to win every point with power',
          'Be patient and wait for opportunities'
        ]
      },
      {
        id: 'pace-control',
        name: 'Pace Control',
        description: 'Control the pace of the game',
        difficulty: 'intermediate',
        prerequisites: ['shot-selection'],
        tips: [
          'Vary the pace of your shots',
          'Use slow shots to set up fast ones',
          'Control the tempo of rallies',
          'Keep your opponent guessing'
        ]
      },
      {
        id: 'advanced-strategy',
        name: 'Advanced Strategy',
        description: 'Develop sophisticated strategic thinking',
        difficulty: 'advanced',
        prerequisites: ['pace-control'],
        tips: [
          'Study your opponent\'s patterns',
          'Use deception and variety',
          'Exploit your opponent\'s weaknesses',
          'Master the mental game'
        ]
      }
    ]
  },
  {
    id: 'mental-game',
    name: 'Mental Game',
    description: 'Develop mental toughness and focus',
    skills: [
      {
        id: 'focus-concentration',
        name: 'Focus & Concentration',
        description: 'Maintain focus throughout the match',
        difficulty: 'beginner',
        tips: [
          'Stay in the present moment',
          'Don\'t dwell on mistakes',
          'Focus on the next point',
          'Use breathing techniques to stay calm'
        ]
      },
      {
        id: 'pressure-handling',
        name: 'Pressure Handling',
        description: 'Perform well under pressure',
        difficulty: 'intermediate',
        prerequisites: ['focus-concentration'],
        tips: [
          'Practice pressure situations',
          'Develop pre-shot routines',
          'Stay positive and confident',
          'Use visualization techniques'
        ]
      },
      {
        id: 'match-strategy',
        name: 'Match Strategy',
        description: 'Develop and execute match strategies',
        difficulty: 'advanced',
        prerequisites: ['pressure-handling'],
        tips: [
          'Adapt your strategy during the match',
          'Exploit your opponent\'s weaknesses',
          'Stay patient and disciplined',
          'Make adjustments as needed'
        ]
      }
    ]
  }
];
